// 261005 E1-P1 — job nền crawl / quét / chia tập do PHIÊN NÀY bắt đầu. Dời từ `AdaptationSource.tsx:62, 109-142`.
// Giữ ở vỏ để đổi chặng không làm mất poll / toast kết thúc.
import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { AdaptationBgStarted } from '../../../services/api/adaptation';
import type { AdaptationCrawlTaskDetail, AdaptationScanTaskDetail } from '../../../types/adaptation';
import { describeCrawlProgress, describeScanProgress } from '../adaptationUtils';
import { useFilmJobPoll } from '../useFilmJobPoll';
import { jobFailedToast } from './jobErrorText';
import { crawlDoneText, decideCrawlTerminal, scanDoneText } from '../scanCostText';

export type BgJobKey = 'crawl' | 'scan' | 'episodes';
export interface BgJobApi {
  bgJob: { key: BgJobKey; id: string } | null;
  progressText: string | null;
  /** Đang gọi API khởi động (trước khi có job id). */
  starting: BgJobKey | null;
  /** Gọi `fn` rồi theo dõi job. Lỗi API ném lại cho nơi gọi xử lý (hộp lỗi có mã). */
  start: (key: BgJobKey, fn: () => Promise<AdaptationBgStarted>, label: string) => Promise<boolean>;
}

/**
 * `candidate` = job nạp/quét/chia tập mà BE báo đang chạy (`pickBgCandidate(workbench.jobs.running)`): sau tải lại / đổi truyện state cục bộ mất
 * ⇒ nhận nuôi để có lại tiến độ (`detail.progress`), toast xong và làm mới dữ liệu (F36). Job đã kết thúc ở phiên này không bị nhận nuôi lại.
 */
export const useBgJob = (storyId: string, onFinished: () => void, candidate: { key: BgJobKey; id: string } | null = null): BgJobApi => {
  const [bgJob, setBgJobState] = useState<{ key: BgJobKey; id: string } | null>(null);
  const doneIds = useRef<Set<string>>(new Set());
  const setBgJob = useCallback((v: { key: BgJobKey; id: string } | null) => {
    setBgJobState((cur) => { if (cur && (!v || v.id !== cur.id)) doneIds.current.add(cur.id); return v; });
  }, []);
  const [progressText, setProgressText] = useState<string | null>(null);
  const [starting, setStarting] = useState<BgJobKey | null>(null);

  /** Đổi truyện → dừng theo dõi job của truyện cũ ngay. */
  useEffect(() => { setBgJobState(null); doneIds.current = new Set(); setProgressText(null); }, [storyId]);
  useEffect(() => {
    if (!bgJob && !starting && candidate && !doneIds.current.has(candidate.id)) setBgJobState(candidate);
  }, [bgJob, starting, candidate?.id, candidate?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback(async (key: BgJobKey, fn: () => Promise<AdaptationBgStarted>, label: string): Promise<boolean> => {
    setStarting(key);
    try {
      const r = await fn();
      if (!r?.jobId) {
        message.warning('Server không trả job id; đã dừng theo dõi và không tự gửi lại.');
        onFinished();
        return false;
      }
      setBgJob({ key, id: r.jobId });
      message.info(`${label} đã bắt đầu ở nền`, 5);
      return true;
    } finally {
      setStarting(null);
    }
  }, [onFinished, setBgJob]);

  useFilmJobPoll<AdaptationCrawlTaskDetail | AdaptationScanTaskDetail>(bgJob?.id ?? null, {
    onTick: (task) => {
      // 261005 E4-P2: `episodes` KHÔNG còn câu giả — tiến độ thật đi qua `workbench.jobs.running[].progress` (xem `bgJobText`).
      setProgressText(
        bgJob?.key === 'crawl'
          ? describeCrawlProgress((task.detail as AdaptationCrawlTaskDetail)?.progress)
          : bgJob?.key === 'scan'
            ? describeScanProgress((task.detail as AdaptationScanTaskDetail)?.progress)
            : null,
      );
    },
    onTerminal: async (outcome, task) => {
      // 261005 E4-P3 (B1): crawl xong có `scanJobId` ⇒ nối sang theo dõi job quét, KHÔNG xoá `bgJob`.
      const chain = decideCrawlTerminal(outcome, bgJob?.key, task);
      if (chain) {
        setBgJob({ key: 'scan', id: chain.chainToScan });
        setProgressText(null);
        message.info(crawlDoneText((task as any)?.detail?.result, true));
        onFinished();   // tải lại danh sách chương vừa nạp
        return;
      }
      if (outcome === 'completed') message.success(bgJob?.key === 'scan' ? scanDoneText((task as any)?.detail?.result) : bgJob?.key === 'crawl' ? crawlDoneText((task as any)?.detail?.result, false) : 'Đã xong.');
      else if (outcome === 'failed') message.error(jobFailedToast((task as any)?.detail?.error, (task as any)?.detail?.note));
      else message.warning('Đã dừng theo dõi — job có thể vẫn chạy, bấm Tải lại.');
      setBgJob(null);
      setProgressText(null);
      if (outcome === 'completed' || outcome === 'failed') onFinished();
    },
  });

  return { bgJob, progressText, starting, start };
};
